import { type FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isNil, _isNotEmpty, _notNil } from '@/littledash';

import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMeta } from '@/support/hot';
import { createMetaV16 } from '@/support/hot-v16';
import Http from '@/support/http';
import { api as apiRoutes, web as webRoute } from '@/support/route';
import { clearSessionStorage, successToast } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';

// @ts-expect-error — v12 @handsontable/react has no type declarations
import type HotTable from '@handsontable/react';

import { getMetaColumns, getMetaData, initCageSettings, initialBulkCageData } from '../Colony.utils';
import { initBulkCageSettingsV16, initialBulkCageDataV16 } from '../Colony.utils.v16';
import '../Colony.scss';

const SpreadSheet = lazy(() => import('@/components/UI/SpreadSheet'));
const BulkCagesTableV16 = lazy(() => import('./BulkCagesTableV16'));

interface BulkCageRow {
  id: ID;
  catalog: string;
  name: string;
  population: number;
  meta: Record<string, MetadataFieldValueTypes>;
}

interface CageSavePayload {
  name: string;
  id: ID;
  animal_count: number;
  meta: ReturnType<typeof createMeta> | ReturnType<typeof createMetaV16> | Array<never>;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const BulkCageEdit: FC = () => {
  const navigate = useNavigate();
  const { hotUpgrade } = useSelector(hotUpgradeSelector);

  const editCageRef = useRef<HotTable>(null);
  const refV16 = useRef<HotTableRef>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField> | false>(false);
  const [metaColumns, setMetaColumns] = useState<Array<MetadataField> | false>(false);
  const [data, setData] = useState<Array<BulkCageRow>>([]);
  const [settings, setSettings] = useState<CustomGridSettings | SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const selectedCages: Array<Cage> = useMemo(
    () => JSON.parse(sessionStorage.getItem('selectedColonyCages') ?? '[]'),
    []
  );

  useEffect(() => {
    return clearSessionStorage('selectedColonyCages', '/colony/edit/cages');
  }, []);

  const fetchMetadata = async (): Promise<void> => {
    setLoading(true);
    try {
      const metadataResponse = await getMetaData(MetadataTypes.collection_meta);
      const responseData = (metadataResponse?.body ?? []) as Array<MetadataField>;
      setMetadata(responseData);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
    if (hotUpgrade) {
      setData(initialBulkCageDataV16(selectedCages) as Array<BulkCageRow>);
    } else {
      setData(initialBulkCageData(selectedCages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotUpgrade]);

  useEffect(() => {
    if (metaColumns === false && _isNotEmpty(data)) {
      if (metadata !== false && _isNotEmpty(metadata)) {
        setMetaColumns(getMetaColumns(selectedCages, metadata));
      } else {
        setMetaColumns([]);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (Array.isArray(metaColumns) && _isNil(settings)) {
      const metadataArray = metadata !== false ? metadata : [];

      if (hotUpgrade) {
        const updateSettings = initBulkCageSettingsV16(metaColumns, metadataArray);
        if (_notNil(updateSettings.settings)) {
          setSettings(updateSettings.settings);
        }
        if (_notNil(updateSettings.metadata)) {
          setMetadata(updateSettings.metadata);
        }
      } else {
        const updateSettings = initCageSettings(true, false, metaColumns, metadataArray) as {
          settings?: CustomGridSettings;
          metadata?: Array<MetadataField>;
        };
        if (_notNil(updateSettings.settings)) {
          setSettings(updateSettings.settings);
        }
        if (_notNil(updateSettings.metadata)) {
          setMetadata(updateSettings.metadata);
        }
      }
    }
  }, [metaColumns]);

  const putCages = async (cages: Array<CageSavePayload>): Promise<void> => {
    if (_isNotEmpty(cages)) {
      await Http.put(apiRoutes('cages.updateMany'), {
        cages,
        query: 'meta',
      });
    }
  };

  const bulkCagesSave = async (): Promise<void> => {
    if (isSaving || _isNil(settings)) {
      return;
    }

    setIsSaving(true);
    const cages: Array<CageSavePayload> = data.map(({ id, name, population, meta }) => {
      let metaPayload: CageSavePayload['meta'] = [];
      if (_isNotEmpty(meta)) {
        if (hotUpgrade) {
          const v16Settings = settings as SpreadsheetV16Settings;
          metaPayload = createMetaV16(meta, v16Settings.columns ?? []);
        } else {
          metaPayload = createMeta(meta, (settings as CustomGridSettings).columns);
        }
      }
      return {
        name,
        id,
        animal_count: population,
        meta: metaPayload,
      };
    });
    try {
      await putCages(cages);
      navigate('/colony');
      successToast(`Successfully edited ${cages.length > 1 ? 'cages' : 'cage'}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetMetadata = (newMetadata: Array<MetadataField>): void => {
    setMetadata(newMetadata);
  };

  const spreadSheet = _notNil(settings) && (
    <>
      {hotUpgrade ? (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <BulkCagesTableV16
              data={data as unknown as Array<Record<string, unknown>>}
              settings={settings as SpreadsheetV16Settings}
              innerRef={refV16 as React.RefObject<HotTableRef>}
              metadata={metadata !== false ? metadata : []}
              setMetadata={handleSetMetadata}
              setInvalid={setIsInvalid}
              maxRows={data.length}
            />
          </div>
        </Suspense>
      ) : (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <SpreadSheet
              data={data}
              settings={settings as CustomGridSettings}
              className="pl4 pr4"
              innerRef={editCageRef}
              metadata={metadata !== false ? metadata : undefined}
              setMetadata={handleSetMetadata}
              maxRows={data.length}
              setInvalid={setIsInvalid}
            />
          </div>
        </Suspense>
      )}
      <div className="pl4 pt4">
        <Button
          onClick={bulkCagesSave}
          disabled={loading || isInvalid || isSaving}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button plain className="ml3" onClick={() => navigate('/colony')}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <>
          <SubHeader linkToText="Colony" link={webRoute('colony')} />
          <div className="ph4">
            <Header mainHeaderText="Edit cages" />
          </div>
          {_isNotEmpty(selectedCages) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any cages"
                text={'Please select cages from inside Colony to proceed'}
                NoDataComponent={
                  <Link to={webRoute('colony')} className="link blue">
                    Go to Colony
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkCageEdit;
